import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { toast, Toaster } from "sonner";
import {
  Check, Minus, ArrowLeft, ArrowRight, Sparkles, MapPin, Search, LineChart,
  BadgeCheck, MessageCircle, IdCard, Star, Camera, ClipboardList,
  Lock, AlertCircle, Loader2, Clock,
} from "lucide-react";

// Perks shown ON each plan card. Verified against the live implementation:
// Free ranking uses a blended score; Pro Plus adds a small boost (+0.6),
// featured/showcase eligibility, per-day view trends + engagement leads,
// a 2nd Wall post/day, messaging neighbors who invite contact, and a badge.
const FREE_PERKS = [
  { Icon: IdCard, text: "Professional profile & business card" },
  { Icon: ClipboardList, text: "Business info & up to 3 service categories" },
  { Icon: Search, text: "Show up in search & discovery" },
  { Icon: Star, text: "Collect reviews & recommendations" },
  { Icon: MessageCircle, text: "Message neighbors directly" },
  { Icon: Camera, text: "Photos, posts & promotions" },
];

const PLUS_PERKS = [
  { Icon: Sparkles, title: "Featured opportunities", desc: "Added visibility in select areas of LIMYA" },
  { Icon: MapPin, title: "Local spotlight eligibility", desc: "Be considered for featured rotations like Pros of the Week" },
  { Icon: Search, title: "Added search visibility", desc: "Appear a little more prominently in relevant discovery" },
  { Icon: LineChart, title: "Performance insights", desc: "Profile-view trends, plus see who's engaging with your posts" },
  { Icon: BadgeCheck, title: "Pro Plus badge", desc: "A subtle badge on your professional profile" },
];

// Comparison rows — booleans render a check/dash; strings render as-is.
const COMPARE = [
  ["Professional profile & business card", true, true],
  ["Business info & up to 3 service categories", true, true],
  ["Search & discovery presence", true, true],
  ["Reviews & recommendations", true, true],
  ["Direct messaging with neighbors", true, true],
  ["Photos, posts & promotions", true, true],
  ["Basic profile stats", true, true],
  ["Wall posts per day", "1", "2"],
  ["Featured opportunities", false, true],
  ["Pros of the Week eligibility", false, true],
  ["Added search visibility", false, true],
  ["Profile-view trends & engagement insights", false, true],
  ["Message neighbors who invite contact", false, true],
  ["Pro Plus badge", false, true],
];

function Cell({ v, plus }) {
  if (v === true) return <Check size={18} className={plus ? "text-[#D6B977]" : "text-[#2E6B57]"} strokeWidth={2.5}/>;
  if (v === false) return <Minus size={16} className="text-[#2D2926]/25"/>;
  return <span className="text-sm font-semibold text-[#2D2926]">{v}</span>;
}

export default function Upgrade() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [program, setProgram] = useState(null);
  const [beta, setBeta] = useState(null);

  useEffect(() => {
    api.get("/config/public").then(r => {
      setPricing(r.data?.pro_plus_pricing || null);
      setProgram(r.data?.pro_plus_program || null);
      setBeta(r.data?.beta || null);
    }).catch(() => {});
  }, []);

  const sessionId = new URLSearchParams(location.search).get("session_id");
  const isSuccess = location.pathname.endsWith("/success");

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setStatus(data);
        if (data.payment_status === "paid") { toast.success("Welcome to Pro Plus! 🎉"); await refresh(); return; }
        if (data.status === "expired" || attempts >= 8) return;
        attempts++;
        setTimeout(poll, 2500);
      } catch {}
    };
    poll();
    // eslint-disable-next-line
  }, [sessionId]);

  // Price comes only from the Stripe Price via /config/public. Never hardcoded,
  // so the page can't advertise an amount Stripe wouldn't charge.
  const price = pricing?.available ? pricing.display : null;
  const amount = pricing?.available ? pricing.amount_display : null;
  const enrollmentOpen = program ? program.enrollment_enabled : null;
  const comingSoon = enrollmentOpen === false;

  const checkout = async () => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "pro") { toast.error("Only professionals can upgrade to Pro Plus."); return; }
    if (comingSoon) { toast.info("Pro Plus enrollment is opening soon."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.status === 403
        ? (e.response?.data?.detail || "Pro Plus enrollment is not open yet.")
        : "Could not start checkout");
      setLoading(false);
    }
  };

  // Free Pro CTA — NEVER touches Stripe. Reuses the existing pro signup flow
  // (role preselected). Signed-in pros already have a Free Pro account.
  const goFreePro = () => {
    if (user?.role === "pro") { toast.success("You already have a Free Pro account."); navigate("/dashboard"); return; }
    navigate("/register?role=pro");
  };

  // Success page must reflect REAL entitlement. A logged-out visitor or a
  // payment that hasn't been confirmed by Stripe never sees a success state.
  if (isSuccess) {
    const confirmed = !!user && user.role === "pro"
      && (user.is_pro_plus || status?.payment_status === "paid");
    const failed = status && (status.status === "expired"
      || ["unpaid", "failed", "canceled"].includes(status.payment_status));
    const pending = !confirmed && !failed;

    return (
      <div className="bg-[#F5F7F5] min-h-screen">
        <Header/><Toaster position="top-center"/>
        <div className="max-w-xl mx-auto px-6 py-20 text-center">
          {!user ? (
            <div data-testid="upgrade-success-signin">
              <div className="w-20 h-20 rounded-full bg-[#2D2926]/10 grid place-items-center mx-auto mb-6"><Lock size={32} className="text-[#2D2926]/50"/></div>
              <h1 className="text-4xl font-bold mb-3 text-[#2D2926]" style={{fontFamily:'Outfit'}}>Please sign in</h1>
              <p className="text-[#2D2926]/60 mb-6">Sign in to your professional account to see your Pro Plus membership status.</p>
              <button onClick={() => navigate("/login")} className="btn-lime px-7 py-3" data-testid="upgrade-success-login-btn">Sign in</button>
            </div>
          ) : confirmed ? (
            <div data-testid="upgrade-success-confirmed">
              <div className="w-20 h-20 rounded-full bg-[#2E6B57] grid place-items-center mx-auto mb-6 shadow-xl"><Check size={36} className="text-white"/></div>
              <h1 className="text-4xl font-bold mb-3 text-[#2D2926]" style={{fontFamily:'Outfit'}}>You're Pro Plus now ✨</h1>
              <p className="text-[#2D2926]/60 mb-6">Your added visibility, performance insights, and Pro Plus badge are all switched on. You can manage or cancel your membership anytime.</p>
              <button onClick={() => navigate("/dashboard")} className="btn-lime px-7 py-3" data-testid="upgrade-success-dashboard">Go to dashboard</button>
            </div>
          ) : failed ? (
            <div data-testid="upgrade-success-failed">
              <div className="w-20 h-20 rounded-full bg-amber-100 grid place-items-center mx-auto mb-6"><AlertCircle size={34} className="text-amber-700"/></div>
              <h1 className="text-4xl font-bold mb-3 text-[#2D2926]" style={{fontFamily:'Outfit'}}>Payment wasn't completed</h1>
              <p className="text-[#2D2926]/60 mb-6">Your card was not charged and your account is unchanged. You can try again whenever you're ready.</p>
              <button onClick={() => navigate("/upgrade")} className="btn-lime px-7 py-3" data-testid="upgrade-success-retry">Back to plans</button>
            </div>
          ) : (
            <div data-testid="upgrade-success-pending">
              <div className="w-20 h-20 rounded-full bg-[#EEF2EF] grid place-items-center mx-auto mb-6"><Loader2 size={32} className="text-[#245544] animate-spin"/></div>
              <h1 className="text-4xl font-bold mb-3 text-[#2D2926]" style={{fontFamily:'Outfit'}}>Confirming your payment…</h1>
              <p className="text-[#2D2926]/60 mb-6">We're waiting for Stripe to confirm. This usually takes a few seconds — nothing is active until it's confirmed.</p>
              <button onClick={() => navigate("/dashboard")} className="text-sm text-[#245544] font-semibold underline" data-testid="upgrade-success-dashboard-link">Go to dashboard</button>
            </div>
          )}
          {status && pending && <p className="text-sm text-[#2D2926]/45 mt-6">Payment status: <b data-testid="upgrade-success-status">{status.payment_status}</b></p>}
        </div>
        <Footer/>
      </div>
    );
  }

  const alreadyPlus = user?.is_pro_plus;
  const betaActive = beta?.active;
  const trial = user?.trial;
  // Pro Plus is a Professional membership — clients and visitors never get a
  // purchase CTA, though they may still see the price informationally.
  const canPurchase = user?.role === "pro";

  return (
    <div className="bg-[#F5F7F5] min-h-screen">
      <Header/><Toaster position="top-center"/>
      <div className="max-w-5xl mx-auto px-6 py-12">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#2D2926]/55 hover:text-[#2D2926] mb-8" data-testid="upgrade-back"><ArrowLeft size={15}/> Back</button>

        {/* Intro — nails the 5-second test: free is real, Plus is optional */}
        <div className="text-center max-w-2xl mx-auto mb-12" data-testid="upgrade-intro">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#2E6B57] mb-3">Professional plans</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#2D2926] mb-4" style={{fontFamily:'Outfit'}}>Every pro is on LIMYA for free</h1>
          <p className="text-[17px] text-[#5C554E] leading-relaxed">
            Pro Plus is an optional {amount ? `${amount}/month` : "monthly"} upgrade for a bit of added visibility, insights, and a few handy extras. Start free and add it whenever the perks are useful.
          </p>
          {comingSoon && (
            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F3EEE2] border border-[#D6B977]/50" data-testid="upgrade-coming-soon-banner">
              <Clock size={15} className="text-[#8A6D2F]"/>
              <span className="text-sm font-semibold text-[#8A6D2F]">Pro Plus enrollment opens soon{price ? ` — ${price}` : ""}</span>
            </div>
          )}
        </div>

        {/* Plan cards */}
        <div className="grid md:grid-cols-2 gap-6 items-stretch" data-testid="upgrade-plan-cards">

          {/* FREE PRO — respectable, real plan */}
          <div className="bg-white border border-[#D3DAD5] rounded-3xl p-8 flex flex-col" data-testid="plan-free-pro">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#2D2926]/50">Free Pro</p>
              <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#EEF2EF] text-[#245544]">No card needed</span>
            </div>
            <div className="flex items-end gap-2 mb-3">
              <span className="text-5xl font-bold text-[#2D2926]" style={{fontFamily:'Outfit'}}>Free</span>
            </div>
            <p className="text-sm text-[#5C554E] mb-6 leading-relaxed">Everything you need to create your professional presence on LIMYA.</p>
            <ul className="space-y-3 mb-8 flex-1">
              {FREE_PERKS.map(({ Icon, text }, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`free-perk-${i}`}>
                  <span className="w-7 h-7 rounded-full bg-[#EEF2EF] grid place-items-center shrink-0"><Icon size={14} className="text-[#245544]"/></span>
                  <span className="text-sm text-[#2D2926]/80 pt-1">{text}</span>
                </li>
              ))}
            </ul>
            <button onClick={goFreePro} className="w-full py-3 rounded-full border-2 border-[#245544] text-[#245544] font-bold hover:bg-[#245544] hover:text-white transition" data-testid="free-pro-cta">
              Create Free Pro Account
            </button>
          </div>

          {/* PRO PLUS — premium charcoal, $7 up top */}
          <div className="relative bg-[#222321] text-white rounded-3xl p-8 flex flex-col shadow-[0_20px_50px_rgba(34,35,33,0.28)] ring-1 ring-[#D6B977]/25" data-testid="plan-pro-plus">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs uppercase tracking-[0.18em] font-bold text-[#D6B977]">LIMYA Pro Plus</p>
              <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white/10 text-white/80">Optional</span>
            </div>
            <div className="flex items-end gap-2 mb-1.5">
              <span className="text-5xl font-bold" style={{fontFamily:'Outfit'}} data-testid="upgrade-price">{price || "—"}</span>
            </div>
            <p className="text-sm text-white/55 mb-4">
              {comingSoon ? "Coming soon — enrollment opens shortly." : "Cancel anytime."}
              {" · "}
              <a href="/legal/membership" target="_blank" rel="noreferrer" className="underline hover:text-white/80" data-testid="upgrade-membership-terms-link">Membership &amp; Billing Terms</a>
            </p>
            <h2 className="text-xl font-bold mb-2" style={{fontFamily:'Outfit'}} data-testid="upgrade-headline">Extra visibility when you want it</h2>
            <p className="text-sm text-white/65 mb-6 leading-relaxed">Free Pro, plus a bit of added visibility, insights, and a few extra tools — for pros who want them.</p>

            <ul className="space-y-3.5 mb-7 flex-1">
              {PLUS_PERKS.map(({ Icon, title, desc }, i) => (
                <li key={i} className="flex items-start gap-3" data-testid={`plus-perk-${i}`}>
                  <span className="w-8 h-8 rounded-full bg-[#D6B977]/15 grid place-items-center shrink-0"><Icon size={15} className="text-[#D6B977]"/></span>
                  <div>
                    <p className="font-semibold text-[15px] leading-tight">{title}</p>
                    <p className="text-[13px] text-white/55 mt-0.5">{desc}</p>
                  </div>
                </li>
              ))}
            </ul>

            {alreadyPlus ? (
              <div className="bg-[#2E6B57]/20 border border-[#2E6B57]/40 rounded-xl p-4 text-center" data-testid="upgrade-already-plus">
                <p className="font-semibold text-[#8FD3BB]">You're a Pro Plus member.</p>
                <button onClick={() => navigate("/dashboard")} className="text-sm text-white/70 hover:text-white underline mt-1" data-testid="upgrade-manage-link">Manage in dashboard</button>
              </div>
            ) : comingSoon ? (
              <>
                {/* Paused: features and price stay visible to build interest,
                    but enrollment is disabled (and blocked server-side). */}
                <button disabled className="w-full py-3.5 rounded-full bg-white/10 text-white/50 font-bold cursor-not-allowed flex items-center justify-center gap-2" data-testid="upgrade-coming-soon-btn">
                  <Clock size={16}/> Coming Soon
                </button>
                <p className="text-[13px] text-white/55 mt-3 text-center" data-testid="upgrade-coming-soon-note">
                  {canPurchase
                    ? <>Pro Plus isn't open for enrollment just yet. Your Free Pro account already works in full — we'll let you know the moment Pro Plus goes live.</>
                    : <>Pro Plus is a membership for professionals on LIMYA.</>}
                </p>
                {canPurchase && (
                  <button onClick={goFreePro} className="w-full text-center text-sm text-white/60 hover:text-white mt-3 transition" data-testid="pro-plus-card-free-link">
                    Keep using <span className="font-semibold underline">Free Pro</span> in the meantime
                  </button>
                )}
              </>
            ) : !canPurchase ? (
              <>
                {/* Clients/visitors: informational only, never a purchase CTA. */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center" data-testid="upgrade-pro-only-note">
                  <p className="text-sm text-white/70">Pro Plus is a membership for professionals on LIMYA.</p>
                </div>
                <button onClick={goFreePro} className="w-full text-center text-sm text-white/60 hover:text-white mt-3 transition" data-testid="pro-plus-card-free-link">
                  Are you a pro? <span className="font-semibold underline">Create a Free Pro account</span>
                </button>
              </>
            ) : (
              <>
                <button onClick={checkout} disabled={loading} className="w-full py-3.5 rounded-full bg-[#2E6B57] hover:bg-[#245544] text-white font-bold transition disabled:opacity-60" data-testid="upgrade-checkout-btn">
                  {loading ? "Starting checkout…" : "Upgrade to Pro Plus"}
                </button>
                <button onClick={goFreePro} className="w-full text-center text-sm text-white/60 hover:text-white mt-3 transition" data-testid="pro-plus-card-free-link">
                  Prefer to start free? <span className="font-semibold underline">Create a Free Pro account</span>
                </button>
                <p className="text-[11px] text-white/35 mt-3 text-center">Secure checkout via Stripe · cancel anytime</p>
              </>
            )}
          </div>
        </div>

        {/* Own 14-day trial — never implies an automatic charge */}
        {trial?.trial_active && !alreadyPlus && (
          <div className="mt-6 bg-[#EEF2EF] border border-[#D3DAD5] rounded-2xl px-5 py-3.5 flex items-start gap-3" data-testid="upgrade-trial-note">
            <Sparkles size={16} className="text-[#245544] shrink-0 mt-0.5"/>
            <p className="text-[13px] text-[#2D2926]/70 leading-relaxed">
              <b className="text-[#245544]">Your complimentary Pro Plus trial is active</b>
              {trial.trial_days_remaining != null ? ` — ${trial.trial_days_remaining} day${trial.trial_days_remaining === 1 ? "" : "s"} left` : ""}.
              No credit card required and you won't be charged automatically. When it ends you'll simply stay on Free Pro unless you choose to subscribe{price ? ` for ${price}` : ""}.
            </p>
          </div>
        )}

        {/* Beta note — understated, never implies an automatic charge */}
        {betaActive && !trial?.trial_active && (
          <div className="mt-6 bg-[#EEF2EF] border border-[#D3DAD5] rounded-2xl px-5 py-3.5 flex items-start gap-3" data-testid="upgrade-beta-note">
            <Sparkles size={16} className="text-[#245544] shrink-0 mt-0.5"/>
            <p className="text-[13px] text-[#2D2926]/70 leading-relaxed">
              <b className="text-[#245544]">Pro Plus perks are included during the public beta</b>{beta?.days_remaining != null ? ` (${beta.days_remaining} days left)` : ""} — no credit card required. You won't be charged automatically; when the beta ends you'll simply stay on Free Pro unless you choose to subscribe.
            </p>
          </div>
        )}

        {/* Comparison */}
        <div className="mt-16" data-testid="upgrade-comparison">
          <div className="text-center max-w-xl mx-auto mb-8">
            <h2 className="text-3xl font-bold text-[#2D2926] mb-2" style={{fontFamily:'Outfit'}}>Choose what works for you</h2>
            <p className="text-[#5C554E]">Start free and add Pro Plus whenever the extra perks are useful.</p>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block bg-white border border-[#D3DAD5] rounded-3xl overflow-hidden">
            <div className="grid grid-cols-[1fr_150px_150px] items-center px-6 py-4 border-b border-[#D3DAD5] bg-[#FAFBFA]">
              <span className="text-sm font-bold text-[#2D2926]" style={{fontFamily:'Outfit'}}>Feature</span>
              <span className="text-center text-sm font-bold text-[#2D2926]/70">Free Pro</span>
              <span className="text-center text-sm font-bold text-[#2D2926] flex items-center justify-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-[#D6B977]"/> Pro Plus</span>
            </div>
            {COMPARE.map(([label, f, p], i) => (
              <div key={i} className={`grid grid-cols-[1fr_150px_150px] items-center px-6 py-3.5 ${i % 2 ? "bg-[#FAFBFA]" : "bg-white"}`} data-testid={`compare-row-${i}`}>
                <span className="text-sm text-[#2D2926]/80">{label}</span>
                <span className="flex justify-center"><Cell v={f}/></span>
                <span className="flex justify-center"><Cell v={p} plus/></span>
              </div>
            ))}
            <div className="grid grid-cols-[1fr_150px_150px] items-center px-6 py-4 border-t border-[#D3DAD5] bg-[#FAFBFA]">
              <span className="text-sm font-bold text-[#2D2926]" style={{fontFamily:'Outfit'}}>Price</span>
              <span className="text-center text-sm font-bold text-[#2D2926]">Free</span>
              <span className="text-center text-sm font-bold text-[#245544]" data-testid="compare-price-plus">{price || "—"}</span>
            </div>
          </div>

          {/* Mobile stacked cards */}
          <div className="md:hidden space-y-2.5">
            {COMPARE.map(([label, f, p], i) => (
              <div key={i} className="bg-white border border-[#D3DAD5] rounded-2xl p-4" data-testid={`compare-card-${i}`}>
                <p className="text-sm font-semibold text-[#2D2926] mb-2.5">{label}</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center justify-between rounded-lg bg-[#FAFBFA] px-3 py-2">
                    <span className="text-xs text-[#2D2926]/55">Free Pro</span><Cell v={f}/>
                  </div>
                  <div className="flex items-center justify-between rounded-lg bg-[#222321] px-3 py-2">
                    <span className="text-xs text-white/60">Pro Plus</span>
                    {p === true ? <Check size={17} className="text-[#D6B977]" strokeWidth={2.5}/> : p === false ? <Minus size={15} className="text-white/30"/> : <span className="text-sm font-semibold text-white">{p}</span>}
                  </div>
                </div>
              </div>
            ))}
            <div className="flex gap-3">
              <button onClick={goFreePro} className="flex-1 py-3 rounded-full border-2 border-[#245544] text-[#245544] font-bold" data-testid="compare-free-cta">Start Free</button>
              {!alreadyPlus && canPurchase && (comingSoon
                ? <button disabled className="flex-1 py-3 rounded-full bg-black/10 text-[#2D2926]/45 font-bold cursor-not-allowed" data-testid="compare-plus-coming-soon">Coming Soon</button>
                : <button onClick={checkout} disabled={loading} className="flex-1 py-3 rounded-full bg-[#2E6B57] text-white font-bold disabled:opacity-60" data-testid="compare-plus-cta">{loading ? "…" : "Get Pro Plus"}</button>
              )}
            </div>
          </div>
        </div>

        {/* Closing reassurance */}
        <p className="text-center text-sm text-[#5C554E] mt-10 max-w-xl mx-auto">
          Free Pro is a complete professional account — no time limit, no trial. Pro Plus just adds a few extras on top when you want them. <button onClick={() => navigate("/faq")} className="text-[#245544] font-semibold underline inline-flex items-center gap-1" data-testid="upgrade-faq-link">Questions? See our FAQ <ArrowRight size={13}/></button>
        </p>
      </div>
      <Footer/>
    </div>
  );
}
